import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { FileUpload } from '@/components/ui/FileUpload';
import { secureCandidateFormValidation } from '@/utils/security/secureValidation';
import type { NewCandidateFormData } from './types';
import { useJobs } from '@/hooks/useJobs';
import { toast } from 'sonner';
import { z } from 'zod';

interface AddCandidateFormProps {
  onSubmit: (data: NewCandidateFormData) => void;
  isSubmitting: boolean;
  onCancel: () => void;
}

type FormData = z.infer<typeof secureCandidateFormValidation>;

export const AddCandidateForm: React.FC<AddCandidateFormProps> = ({ 
  onSubmit, 
  isSubmitting, 
  onCancel 
}) => {
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const { data: jobs = [], isLoading: jobsLoading } = useJobs();

  const form = useForm<FormData>({
    resolver: zodResolver(secureCandidateFormValidation),
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      source: '',
      status: 'new',
      job_id: '',
    },
  });

  const handleFormSubmit = (values: FormData) => {
    if (!values.email) {
      toast.error('Email is required');
      return;
    }

    const candidateData: NewCandidateFormData = {
      first_name: values.first_name,
      last_name: values.last_name,
      email: values.email,
      phone: values.phone || null,
      source: values.source || null,
      status: values.status || 'new',
      job_id: values.job_id || null,
      resume_file: resumeFile,
    };
    
    onSubmit(candidateData);
  };

  const handleFileSelect = (file: File) => {
    setResumeFile(file);
  };

  const handleFileRemove = () => {
    setResumeFile(null);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleFormSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="first_name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>First Name *</FormLabel>
              <FormControl>
                <Input 
                  placeholder="John" 
                  {...field}
                  maxLength={100}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="last_name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Last Name *</FormLabel>
              <FormControl>
                <Input 
                  placeholder="Doe" 
                  {...field}
                  maxLength={100}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email *</FormLabel>
              <FormControl>
                <Input 
                  type="email" 
                  placeholder="john.doe@example.com" 
                  {...field}
                  maxLength={254}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="phone"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Phone (Optional)</FormLabel>
              <FormControl>
                <Input 
                  type="tel" 
                  placeholder="+1234567890" 
                  {...field}
                  maxLength={20}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="source"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Source (Optional)</FormLabel>
              <FormControl>
                <Input 
                  placeholder="LinkedIn, Referral, etc." 
                  {...field}
                  maxLength={1000}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="job_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Job (Optional)</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a job to apply this candidate to" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {jobs.map((job) => (
                    <SelectItem key={job.id} value={job.id}>
                      {job.title} - {job.company}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="space-y-2">
          <label className="text-sm font-medium">Resume (Optional)</label>
          <FileUpload
            onFileSelect={handleFileSelect}
            onFileRemove={handleFileRemove}
            selectedFile={resumeFile}
            accept=".pdf,.doc,.docx"
            maxSize={10 * 1024 * 1024} // 10MB
          />
        </div>
        
        <div className="flex justify-end space-x-3 pt-4">
          <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Adding...' : 'Add Candidate'}
          </Button>
        </div>
      </form>
    </Form>
  );
};
